/* Beylora 3D viewer launcher.
   Reads its config from #bey3d-config (asset URL map + variant profile map),
   injects a "View in 3D" pill into the product gallery, and opens the exact
   source-renderer viewer in a full-screen same-origin iframe (srcdoc).
   The iframe document remaps the renderer's fixed asset paths onto Shopify CDN
   URLs by filename; supplier bundles are loaded byte-identical, never edited.
   Every entry point is guarded: any failure hides the button and leaves the
   PDP exactly as it was. Closing the modal removes the iframe entirely, which
   disposes the renderer per profile. */
(function () {
  'use strict';

  var configEl = document.getElementById('bey3d-config');
  if (!configEl) return;
  var config;
  try { config = JSON.parse(configEl.textContent); } catch (e) { return; }
  if (!config || !config.enabled || !config.assets || !config.variants || !Object.keys(config.variants).length) return;

  var currentVariantId = null;
  var modal = null;      // open modal (null when closed)
  var modalEl = null;    // the ONE persistent modal element for the session

  function variantProfile(id) {
    return (id && config.variants[String(id)]) || null;
  }

  // Escape "<" so embedded JSON can never terminate a srcdoc <script> block.
  function jsonForScript(value) {
    return JSON.stringify(value).replace(/</g, '\\u003c');
  }

  function selectedVariantId() {
    var urlVariant = new URLSearchParams(location.search).get('variant');
    if (urlVariant) return urlVariant;
    var input = document.querySelector('product-form-component form[action*="/cart/add"] [name="id"]') ||
      document.querySelector('form[action*="/cart/add"] [name="id"]');
    return input ? input.value : null;
  }

  /* ---------- iframe document ---------- */

  function buildViewerDoc(profile) {
    var assets = config.assets;
    var fileMap = {};
    Object.keys(assets).forEach(function (name) { fileMap[name] = assets[name]; });
    // The renderer requests these exact basenames from its original paths; the
    // shim rewrites them onto the CDN copies (prefix "bey3d-").
    var shimMap = {};
    Object.keys(fileMap).forEach(function (name) {
      shimMap[name.replace(/^bey3d-/, '')] = fileMap[name];
    });
    Object.keys(config.glbs || {}).forEach(function (name) {
      shimMap[name] = config.glbs[name]; // this product's models
    });
    shimMap['CameraViews.json'] = fileMap['bey3d-preset.CameraViews.json'];
    shimMap['ajax-loading-y23.gif'] = fileMap['bey3d-360_video_popup.png'];

    var globals = {
      metal: profile.metal,
      carat: profile.carat,
      shape: profile.shape,
      glb: profile.glb.replace(/^bey3d-/, ''),
      productTitle: config.product_title || 'Beylora Ring'
    };

    return [
      '<!DOCTYPE html><html><head><meta charset="utf-8">',
      '<meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover">',
      '<link rel="stylesheet" href="' + fileMap['bey3d-viewer-frame.css'] + '">',
      '<script>(function(){var m=["log","warn","error","info","debug"];m.forEach(function(k){var o=console[k]&&console[k].bind(console);if(!o)return;console[k]=function(){var a=[];for(var i=0;i<arguments.length;i++){var x=arguments[i];a.push(x!==null&&typeof x==="object"?(function(){try{return "[obj] "+(x.message||x.type||JSON.stringify(x).slice(0,120))}catch(e){return "[obj]"}})():x)}o.apply(null,a)}})})()<\/script>',
      '<script type="importmap">' + jsonForScript({ imports: {
        '/vendor/meshopt_decoder.module.js': fileMap['bey3d-meshopt_decoder.module.js']
      } }) + '<\/script>',
      '</head><body style="background:#f4f4f4">',
      '<main class="viewer-shell" aria-label="Interactive 3D ring viewer">',
      '<div class="bey3d-status" id="bey3dStatus"><span id="bey3dStatusText">Loading your ring</span><div class="bar"><i></i></div></div>',
      '<div id="view360Modal"></div>',
      '<div id="new_360_video"></div>',
      '</main>',
      '<script>window.BEY3D_SHIM_MAP=' + jsonForScript(shimMap) + ';',
      'window.BEY3D_GLOBALS=' + jsonForScript(globals) + ';<\/script>',
      '<script>' + shimSource() + '<\/script>',
      '<script type="module">',
      'import { MeshoptDecoder } from "' + fileMap['bey3d-meshopt_decoder.module.js'] + '";',
      'window.MeshoptDecoder = MeshoptDecoder;',
      'window.dispatchEvent(new Event("bey3d:meshopt-ready"));',
      '<\/script>',
      '<script>' + bootSource() + '<\/script>',
      '</body></html>'
    ].join('');
  }

  /* Path shim: rewrite renderer asset requests by basename onto Shopify CDN.
     A "#<basename>" fragment is appended so the renderer's extension-based
     importer still sees the original file type behind the CDN query string. */
  function shimSource() {
    return '(' + String(function () {
      var MAP = window.BEY3D_SHIM_MAP;
      function remap(url) {
        try {
          if (typeof url !== 'string') return url;
          if (url.indexOf('data:') === 0 || url.indexOf('blob:') === 0) return url;
          var base = url.split('#')[0].split('?')[0].split('/').pop();
          if (MAP[base]) return MAP[base] + '#' + base;
          return url;
        } catch (e) { return url; }
      }
      var origFetch = window.fetch;
      window.fetch = function (input, init) {
        try {
          if (typeof input === 'string') return origFetch.call(this, remap(input), init);
          if (input instanceof URL) return origFetch.call(this, remap(String(input)), init);
          // three.js FileLoader and the runtime's asset wrapper pass Request objects
          if (input && typeof input.url === 'string') {
            var mapped = remap(input.url);
            if (mapped !== input.url) return origFetch.call(this, new Request(mapped, input), init);
          }
        } catch (e) { /* fall through to original */ }
        return origFetch.call(this, input, init);
      };
      var origOpen = XMLHttpRequest.prototype.open;
      XMLHttpRequest.prototype.open = function (method, url) {
        arguments[1] = remap(url);
        return origOpen.apply(this, arguments);
      };
      new MutationObserver(function (records) {
        records.forEach(function (r) {
          var nodes = [];
          if (r.type === 'attributes' && r.target.tagName === 'IMG') nodes = [r.target];
          if (r.addedNodes) r.addedNodes.forEach(function (n) {
            if (n.tagName === 'IMG') nodes.push(n);
            else if (n.querySelectorAll) nodes.push.apply(nodes, n.querySelectorAll('img'));
          });
          nodes.forEach(function (img) {
            var mapped = remap(img.getAttribute('src') || '');
            if (mapped && mapped !== img.getAttribute('src')) img.src = mapped;
          });
        });
      }).observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['src'] });
    }) + ')()';
  }

  /* Viewer boot inside the iframe: PDP state contract + sequential bundle load
     + mount event + rendered-frame readiness gate (same logic proven in the
     local parity fixture). */
  function bootSource() {
    return '(' + String(function () {
      var G = window.BEY3D_GLOBALS;
      var statusEl = document.getElementById('bey3dStatus');
      var statusText = document.getElementById('bey3dStatusText');
      var bootT0 = performance.now();
      statusEl.style.opacity = '0'; // warm remounts finish before this shows
      setTimeout(function () { if (!ready) statusEl.style.opacity = '1'; }, 600);
      function fail(msg) {
        statusText.textContent = msg;
        parent.postMessage({ bey3d: 'error' }, '*');
      }
      Object.assign(window, {
        dataLayer: [],
        react_360_GA: {},
        react_add_to_bag_click: function () {},
        react_add_to_bag_text: '',
        react_carat_range: {},
        react_change_carat: function () {},
        react_change_shape: function () {},
        react_current_carat: G.carat,
        react_current_metal: G.metal,
        react_current_ring_size: '',
        react_current_shape: G.shape,
        react_has_carat_slider: false,
        react_is_gemstone: false,
        react_new_360_video_token: '',
        react_new_360_viewer_url: 'assets/models/' + G.glb,
        react_primary_collection: {},
        react_product_active: true,
        react_product_id: '82581085',
        react_product_name: G.productTitle,
        react_rating_position: 130,
        react_rating_width: '100',
        react_select_size_options: [],
        react_shape_range: [G.shape],
        react_total_review_count: 0,
        react_wishlist_is_checked: false,
        react_wishlist_click: function () {},
        track_event: function () {},
        track_event_for_ga4: function () {},
        build_ga4_ecommerce_data: function () { return {}; }
      });

      function loadScript(src) {
        return new Promise(function (resolve, reject) {
          var s = document.createElement('script');
          s.src = src; s.async = false;
          s.onload = resolve;
          s.onerror = function () { reject(new Error('load failed: ' + src)); };
          document.head.appendChild(s);
        });
      }

      var ready = false;
      var canvasSince = 0;
      // A 5-point probe missed the ring whenever those exact points all landed
      // on background (narrow ring, tall phone canvas, mid-load pose), so the
      // overlay never lifted on slow connections: 7 of 25 personas hit it and
      // it reproduced live at 1-2Mbps. Sample a grid instead, and treat a
      // canvas that has been painting for a while as ready regardless, so the
      // label can never outlive a ring the customer can already see.
      function frameHasRing(gl, canvas) {
        if (canvas.width < 2 || canvas.height < 2) return false;
        var sample = new Uint8Array(4);
        var colors = {};
        var n = 0;
        for (var i = 1; i <= 7; i++) {
          for (var j = 1; j <= 7; j++) {
            var x = Math.min(canvas.width - 1, Math.floor(canvas.width * (i / 8)));
            var y = Math.min(canvas.height - 1, Math.floor(canvas.height * (j / 8)));
            gl.readPixels(x, y, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, sample);
            var key = sample.join(',');
            if (!colors[key]) { colors[key] = 1; n++; }
            if (n > 1) return true;
          }
        }
        return false;
      }
      function checkReady() {
        var canvas = document.querySelector('#modal_view_360 canvas[data-engine], #new_360_video canvas[data-engine]');
        if (!canvas) { canvasSince = 0; return false; }
        var painted = canvas.width > 2 && canvas.height > 2;
        if (painted && !canvasSince) canvasSince = performance.now();
        var gl = canvas.getContext('webgl2') || canvas.getContext('webgl');
        // The 49-point grid IS the gate. A time-based "the canvas has been
        // painting a while" fallback was tried and REMOVED: on a throttled
        // connection the canvas exists long before the model finishes
        // downloading, so it dismissed the loader over an empty grey scene
        // and the customer got a blank screen with no explanation (caught by
        // judging a 2Mbps mobile capture). An empty canvas means NOT ready;
        // a genuine failure is caught by the 75s budget in watch().
        if (!gl || !frameHasRing(gl, canvas)) return false;
        if (!ready) {
          ready = true;
          // ALWAYS hide via display none: the 600ms timer sets inline opacity,
          // which defeats any class-based fade (P1 caught by the 25-ICP sweep;
          // inline style beats class rules, the 2026-08-10 hidden-guard law)
          statusEl.style.opacity = '';
          statusEl.style.display = 'none';
          statusEl.classList.add('is-hidden');
          parent.postMessage({ bey3d: 'ready' }, '*');
        }
        return true;
      }
      // Every deadline is anchored to bootT0 (the moment the customer opened
      // the viewer), NOT to whenever the bundles happened to finish. watch()
      // runs after bundle load, so anchoring here let a 1Mbps open run ~86s
      // with no staged copy and no timeout at all (2 personas measured it).
      function watch() {
        var since = function () { return performance.now() - bootT0; };
        var at = function (ms, fn) { setTimeout(fn, Math.max(0, ms - since())); };
        at(14000, function () { if (!ready) statusText.textContent = 'Still loading.'; });
        at(35000, function () { if (!ready) statusText.textContent = 'Almost there.'; });
        (function poll() {
          if (checkReady()) return;
          if (since() >= (window.innerWidth >= 1024 ? 95000 : 75000)) { // desktop canvases are ~4x the pixels; 75s false-alarmed at 1.5Mbps (P5 QA 2026-08-21: ready at 85.5s)
            fail('The 3D view is taking too long on this connection. Close this and the photos and videos above show every angle.');
            // Keep watching at a low rate: on a very slow link the ring can
            // still arrive, and leaving a "taking too long" message sitting
            // over a fully rendered ring is its own defect (seen at t+100s on
            // a 1Mbps capture). checkReady() clears the message if it lands.
            var late = setInterval(function () {
              if (checkReady() || since() > 300000) clearInterval(late);
            }, 1000);
            return;
          }
          requestAnimationFrame(poll);
        })();
      }

      function start() {
        var order = ['vendors.bundle.js', 'react.bundle.js', 'offline-runtime-bridge.js', 'pdp_main.bundle.js'];
        order.reduce(function (p, name) {
          return p.then(function () { return loadScript(window.BEY3D_SHIM_MAP[name]); });
        }, Promise.resolve()).then(function () {
          watch();
          setTimeout(function () { document.dispatchEvent(new Event('view_360_clicked')); }, 80);
          setTimeout(function () {
            if (!document.querySelector('#modal_view_360 canvas, #new_360_video canvas')) {
              document.dispatchEvent(new Event('view_360_clicked'));
            }
          }, 700);
        }).catch(function (e) { fail('The 3D view could not load. Please close and try again.'); });
      }

      if (window.MeshoptDecoder) start();
      else window.addEventListener('bey3d:meshopt-ready', start, { once: true });
      setTimeout(function () { if (!window.MeshoptDecoder && !ready) fail('The 3D view could not load. Please close and try again.'); }, 15000);
    }) + ')()';
  }

  /* ---------- modal ---------- */

  var viewerMetal = null;
  var lastProfile = null;
  var pushedHistory = false;
  var savedScrollY = 0;

  // The phone's back gesture is the natural exit: while the viewer is open it
  // must close the viewer, never leave the page (Joe repro 2026-08-19).
  function onPopState() { if (modal) closeViewer(true); }

  function lockScroll() {
    savedScrollY = window.scrollY;
    document.documentElement.classList.add('bey3d-lock');
    // position-fixed body: survives iOS Safari, which loses scroll under
    // plain overflow:hidden locks
    document.body.style.position = 'fixed';
    document.body.style.top = -savedScrollY + 'px';
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
  }
  function unlockScroll() {
    document.documentElement.classList.remove('bey3d-lock');
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    window.scrollTo(0, savedScrollY);
  }

  function mountViewerFrame(profile, keepIdx) {
    lastProfile = profile;
    if (profile.mode === 'frames') { mountFramesPlayer(profile, keepIdx); return; }
    var oldPlayer = modal.querySelector('.bey3d-fplayer');
    if (oldPlayer) oldPlayer.classList.add('bey3d-hidden');
    // A FRESH iframe per mount. Assigning srcdoc to an EXISTING frame is a
    // child-frame navigation, and those append to the JOINT session history:
    // that, not the theme, is what left an orphan entry between the customer
    // and our pushState entry, so the back gesture stopped closing the viewer
    // after a close+reopen (traced 2026-08-19 with a pre-page init script).
    // A newly inserted frame's first navigation REPLACES instead, so the
    // history stays clean. The old frame is removed, which unloads its realm.
    var iframe = modal.querySelector('.bey3d-frame');
    if (iframe) { try { iframe.remove(); } catch (e) { /* ignore */ } iframe = null; }
    if (!iframe) {
      iframe = document.createElement('iframe');
      iframe.className = 'bey3d-frame';
      iframe.setAttribute('title', (config.product_title || 'Ring') + ' in 3D');
      // in-frame listeners re-attach on every navigation via this one handler
      iframe.addEventListener('load', function () {
        try {
          iframe.contentDocument.addEventListener('keydown', function (e) {
            if (e.key === 'Escape') { closeViewer(); return; }
            // parent listeners go deaf once the frame takes focus, so the
            // modal's key handling is re-attached here on every navigation
            modalKeys(e);
          });
          iframe.contentDocument.addEventListener('pointerdown', function () {
            var hint = modal && modal.querySelector('.bey3d-hint');
            if (hint) hint.classList.add('bey3d-hidden');
          }, { once: true });
        } catch (err) { /* never break close paths */ }
      });
      modal.insertBefore(iframe, modal.querySelector('.bey3d-close'));
    }
    iframe.classList.remove('bey3d-hidden');
    iframe.srcdoc = buildViewerDoc(profile);
  }

  /* ---------- frames player (Petite Pavé: no source mesh exists; the source
     page itself serves a 192-frame photoreal spinshot, so we play those exact
     frames — drag maps to frame index, one viewport width = one revolution.
     Lives in the parent document (no iframe), inside the same modal chrome:
     chip, metal pills, Reset view, and every close path are shared). */

  var framesUrls = null;
  var framesIdx = 0;
  var framesGen = 0;     // preload generation token: bumping cancels stragglers
  var framesCache = null; // strong refs while open; released on close
  var dragState = null;

  function mountFramesPlayer(profile, keepIdx) {
    var iframe = modal.querySelector('.bey3d-frame');
    // remove, never navigate to about:blank: a child-frame navigation appends
    // a joint-session-history entry and breaks the back gesture
    if (iframe) { try { iframe.remove(); } catch (e) { /* ignore */ } }
    var fp = modal.querySelector('.bey3d-fplayer');
    if (!fp) {
      fp = document.createElement('div');
      fp.className = 'bey3d-fplayer';
      fp.innerHTML = '<img class="bey3d-fimg" alt="" draggable="false">' +
        '<div class="bey3d-fstatus">Loading your ring</div>';
      modal.insertBefore(fp, modal.querySelector('.bey3d-close'));
    }
    fp.classList.remove('bey3d-hidden');
    framesUrls = (config.frames && config.frames[profile.metal]) || null;
    var img = fp.querySelector('.bey3d-fimg');
    var status = fp.querySelector('.bey3d-fstatus');
    if (!framesUrls || !framesUrls.length) {
      status.textContent = 'The 3D view could not load. Please close and try again.';
      status.classList.remove('bey3d-hidden');
      return;
    }
    if (!keepIdx) framesIdx = 0; // metal switch keeps the angle; reset returns home
    status.textContent = 'Loading your ring';
    status.classList.remove('bey3d-hidden');
    img.onload = function () { status.classList.add('bey3d-hidden'); img.onload = null; };
    img.onerror = function () { status.textContent = 'The 3D view could not load. Please close and try again.'; };
    img.src = framesUrls[framesIdx];
    preloadFrames(framesUrls);
  }

  // Coarse-then-fine preload (the source player's own strategy): every 8th
  // frame first so a spin is watchable within seconds, then fill the gaps.
  function preloadFrames(urls) {
    var gen = ++framesGen;
    framesCache = [];
    var seq = [];
    for (var i = 0; i < urls.length; i += 8) seq.push(i);
    for (var j = 0; j < urls.length; j++) if (j % 8) seq.push(j);
    var k = 0, inflight = 0;
    (function next() {
      if (gen !== framesGen) return;
      while (inflight < 6 && k < seq.length) {
        var im = new Image();
        inflight++;
        im.onload = im.onerror = function () { inflight--; next(); };
        im.src = urls[seq[k]];
        framesCache.push(im);
        k++;
      }
    })();
  }

  function frameShow(idx) {
    framesIdx = idx;
    var img = modal && modal.querySelector('.bey3d-fimg');
    if (img) img.src = framesUrls[idx];
  }

  function esc(t) {
    return String(t).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  /* The corner chip: what you picked on line one, and what the MODEL is
     actually showing on line two (Joe ruling 2026-08-20). The 3D opens at
     every carat, so whenever the model is a smaller stone than the selection
     the viewer says so plainly rather than letting the customer assume the
     stone on screen is the one they chose. Two lines, because one run-on
     string became unreadable once Platinum was in play too. */
  function chipHtml(profile, metal) {
    var name = (config.product_title || '').replace(/ Lab-Grown Diamond Ring$/, '');
    var ct = parseFloat(profile.carat) > 0 ? ' \u00b7 ' + parseFloat(profile.carat) + 'ct' : '';
    var notes = [];
    if (profile.shown_carat) notes.push('3D model shows a ' + profile.shown_carat + 'ct stone');
    if (profile.platinum_shows_wg && metal === 'WG') notes.push('Platinum, shown in white metal');
    return '<span class="bey3d-chip__main">' + esc(name + ct) + '</span>' +
      (notes.length ? '<span class="bey3d-chip__note">' + esc(notes.join(' \u00b7 ')) + '</span>' : '');
  }

  function setViewerMetal(metal) {
    var profile = variantProfile(currentVariantId);
    if (!modal || !profile || viewerMetal === metal) return;
    viewerMetal = metal;
    // Geometry is shared across metals for every mapped variant; only the
    // source material package changes. Cart selection is untouched.
    // Frames mode keeps the current angle across the switch (keepIdx).
    mountViewerFrame({ mode: profile.mode, glb: profile.glb, metal: metal, carat: profile.carat, shape: profile.shape, platinum_shows_wg: profile.platinum_shows_wg, shown_carat: profile.shown_carat }, true);
    updateMetalSwitch();
    var chip = modal.querySelector('.bey3d-chip');
    if (chip) chip.innerHTML = chipHtml(profile, metal); // caption follows the shown metal
  }

  function updateMetalSwitch() {
    if (!modal) return;
    modal.querySelectorAll('.bey3d-metal').forEach(function (b) {
      b.classList.toggle('is-active', b.dataset.metal === viewerMetal);
      b.setAttribute('aria-pressed', b.dataset.metal === viewerMetal ? 'true' : 'false');
    });
  }

  function openViewer() {
    var profile = variantProfile(currentVariantId);
    if (!profile || modal) return;
    if (modalEl) {
      modal = modalEl;
      modal.classList.remove('bey3d-hidden');
      var oldHint = modal.querySelector('.bey3d-hint');
      if (oldHint) oldHint.classList.remove('bey3d-hidden');
      finishOpen(profile);
      return;
    }
    modal = modalEl = document.createElement('div');
    modal.className = 'bey3d-modal';
    modal.innerHTML =
      '<div class="bey3d-backdrop"></div>' +
      '<button type="button" class="bey3d-close" aria-label="Close 3D view">' +
      '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M2 2l12 12M14 2L2 14"/></svg>' +
      '</button>' +
      '<div class="bey3d-chip"></div>' +
      '<button type="button" class="bey3d-reset" aria-label="Reset the 3D view">' +
      '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">' +
      '<path d="M3 12a9 9 0 1 0 3-6.7"/><path d="M3 4v5h5"/></svg>' +
      '<span>Reset view</span>' +
      '</button>' +
      '<div class="bey3d-metal-switch" role="group" aria-label="Metal">' +
      '<button type="button" class="bey3d-metal" data-metal="WG">White</button>' +
      '<button type="button" class="bey3d-metal" data-metal="YG">Yellow</button>' +
      '</div>' +
      '<div class="bey3d-hint">' +
      ('ontouchstart' in window ? 'Drag to rotate. Pinch to zoom.' : 'Drag to rotate. Scroll to zoom.') +
      '</div>';
    document.body.appendChild(modal);
    finishOpen(profile);
  }

  function finishOpen(profile) {
    viewerMetal = profile.metal;
    var chip = modal.querySelector('.bey3d-chip');
    chip.innerHTML = chipHtml(profile, profile.metal);
    var hintEl = modal.querySelector('.bey3d-hint');
    hintEl.textContent = profile.mode === 'frames' ? 'Drag to spin' :
      ('ontouchstart' in window ? 'Drag to rotate. Pinch to zoom.' : 'Drag to rotate. Scroll to zoom.');
    mountViewerFrame(profile);
    updateMetalSwitch();
    /* The toggle names the metal COLOR the 3D shows (2026-10-24): ring pages offer 14k and 18k white and
       yellow gold and platinum, so "White Gold" over a platinum or 18k ring read as the wrong metal.
       The chip above says Platinum when the white model stands in for it. */
    modal.querySelectorAll('.bey3d-metal').forEach(function (b) {
      var names = { WG: ['White', 'White metal'], YG: ['Yellow', 'Yellow metal'], RG: ['Rose', 'Rose metal'] }[b.dataset.metal];
      if (!names) return;
      b.textContent = names[0];
      b.setAttribute('aria-label', names[1]);
    });
    // Back-gesture contract. NOTE (measured 2026-08-19): the THEME itself
    // writes history entries around our close (history.length grows by 2 on
    // an X-close even when we only replaceState), so after a close+reopen the
    // back gesture lands on a theme entry instead of ours. Two alternatives
    // were built and measured LIVE against this baseline and BOTH were worse
    // (route-close-through-popstate and own-one-entry: 4 dead presses vs 3),
    // so this baseline stands and the residue is reported, not papered over.
    history.pushState({ bey3d: 1 }, '', location.href);
    pushedHistory = true;
    window.addEventListener('popstate', onPopState);
    // hint retires after the first real interaction; the listener is tracked
    // so close always releases it (an orphaned closure here retained the whole
    // detached viewer tree, ~14MB per open: 25-ICP sweep finding)
    var hint = modal.querySelector('.bey3d-hint');
    hintHider = function () {
      if (modal && hint) hint.classList.add('bey3d-hidden');
      window.removeEventListener('pointerdown', hintHider, true);
      hintHider = null;
    };
    window.addEventListener('pointerdown', hintHider, true);
    lockScroll();
    // The theme has document-level capture handlers that stop propagation of
    // trusted pointer/touch events before they reach the button, so the close
    // control listens at WINDOW capture, which runs ahead of all of them.
    var closeBtn = modal.querySelector('.bey3d-close');
    var backdrop = modal.querySelector('.bey3d-backdrop');
    var metalBtns = [].slice.call(modal.querySelectorAll('.bey3d-metal'));
    var resetBtn = modal.querySelector('.bey3d-reset');
    windowClose = function (e) {
      var path = e.composedPath ? e.composedPath() : [];
      if (path.indexOf(closeBtn) !== -1 || path.indexOf(backdrop) !== -1) { closeViewer(); return; }
      if (path.indexOf(resetBtn) !== -1) {
        if (lastProfile) mountViewerFrame(lastProfile); // fresh camera, same model+metal
        return;
      }
      for (var i = 0; i < metalBtns.length; i++) {
        if (path.indexOf(metalBtns[i]) !== -1) { setViewerMetal(metalBtns[i].dataset.metal); return; }
      }
    };
    window.addEventListener('pointerup', windowClose, true);
    window.addEventListener('click', windowClose, true);
    document.addEventListener('keydown', escClose);
    document.addEventListener('keydown', modalKeys);
    // move focus into the modal so Tab starts inside it, not on the page behind
    var firstCtl = modal.querySelector('.bey3d-close');
    if (firstCtl) firstCtl.focus({ preventScroll: true });
    // Frames-mode drag also rides window capture: the theme stops trusted
    // pointer events at document capture, so element listeners never fire.
    // No-ops instantly unless the active profile is a frames player.
    framesDown = function (e) {
      if (!lastProfile || lastProfile.mode !== 'frames' || !modal || !framesUrls) return;
      var fp = modal.querySelector('.bey3d-fplayer');
      if (!fp || fp.classList.contains('bey3d-hidden')) return;
      var path = e.composedPath ? e.composedPath() : [];
      if (path.indexOf(fp) === -1) return;
      dragState = { x: e.clientX, idx: framesIdx, w: Math.max(fp.clientWidth, 200) };
    };
    framesMove = function (e) {
      if (!dragState || !framesUrls) return;
      var n = framesUrls.length;
      var delta = Math.round((e.clientX - dragState.x) / (dragState.w / n));
      var idx = ((dragState.idx - delta) % n + n) % n;
      if (idx !== framesIdx) frameShow(idx);
    };
    framesUp = function () { dragState = null; };
    window.addEventListener('pointerdown', framesDown, true);
    window.addEventListener('pointermove', framesMove, true);
    window.addEventListener('pointerup', framesUp, true);
  }

  function escClose(e) { if (e.key === 'Escape') closeViewer(); }

  /* Keyboard parity (25-ICP a11y findings). The tile promises "every angle",
     so arrows must rotate too; the supplier runtime only listens to pointer
     input, and its bundle is byte-identical by contract, so we SYNTHESIZE a
     drag on its canvas rather than touching its code. Tab is also trapped to
     the modal's own controls: it used to leak onto the page behind. */
  function focusables() {
    if (!modal) return [];
    return [].slice.call(modal.querySelectorAll('.bey3d-close, .bey3d-reset, .bey3d-metal'))
      .filter(function (el) { return el.offsetParent !== null; });
  }

  function nudgeRotate(dx, dy) {
    var target = null, doc = null;
    var fp = modal && modal.querySelector('.bey3d-fplayer');
    if (lastProfile && lastProfile.mode === 'frames') {
      if (!framesUrls) return;
      var n = framesUrls.length;
      frameShow(((framesIdx - Math.round(dx / 12)) % n + n) % n);
      return;
    }
    var fr = modal && modal.querySelector('.bey3d-frame');
    try { doc = fr && fr.contentDocument; } catch (e) { return; }
    if (!doc) return;
    target = doc.querySelector('#modal_view_360 canvas[data-engine], #new_360_video canvas[data-engine]');
    if (!target) return;
    var r = target.getBoundingClientRect();
    var cx = r.left + r.width / 2, cy = r.top + r.height / 2;
    var win = doc.defaultView;
    function send(type, x, y, buttons) {
      var ev;
      try {
        ev = new win.PointerEvent(type, { bubbles: true, cancelable: true, clientX: x, clientY: y, buttons: buttons, pointerId: 1, pointerType: 'mouse', isPrimary: true });
      } catch (e) {
        ev = new win.MouseEvent(type.replace('pointer', 'mouse'), { bubbles: true, cancelable: true, clientX: x, clientY: y, buttons: buttons });
      }
      target.dispatchEvent(ev);
    }
    send('pointerdown', cx, cy, 1);
    send('pointermove', cx + dx / 2, cy + dy / 2, 1);
    send('pointermove', cx + dx, cy + dy, 1);
    send('pointerup', cx + dx, cy + dy, 0);
  }

  function modalKeys(e) {
    if (!modal) return;
    var STEP = e.shiftKey ? 90 : 30;
    if (e.key === 'ArrowLeft') { nudgeRotate(-STEP, 0); e.preventDefault(); return; }
    if (e.key === 'ArrowRight') { nudgeRotate(STEP, 0); e.preventDefault(); return; }
    if (e.key === 'ArrowUp') { nudgeRotate(0, -STEP); e.preventDefault(); return; }
    if (e.key === 'ArrowDown') { nudgeRotate(0, STEP); e.preventDefault(); return; }
    if (e.key !== 'Tab') return;
    var list = focusables();
    if (!list.length) return;
    var i = list.indexOf(document.activeElement);
    var next = e.shiftKey ? (i <= 0 ? list.length - 1 : i - 1) : (i === -1 || i === list.length - 1 ? 0 : i + 1);
    list[next].focus();
    e.preventDefault();
  }

  function closeViewer(fromPop) {
    if (!modal) return;
    document.removeEventListener('keydown', escClose);
    document.removeEventListener('keydown', modalKeys);
    window.removeEventListener('popstate', onPopState);
    if (hintHider) { window.removeEventListener('pointerdown', hintHider, true); hintHider = null; }
    if (windowClose) {
      window.removeEventListener('pointerup', windowClose, true);
      window.removeEventListener('click', windowClose, true);
      windowClose = null;
    }
    if (framesDown) {
      window.removeEventListener('pointerdown', framesDown, true);
      window.removeEventListener('pointermove', framesMove, true);
      window.removeEventListener('pointerup', framesUp, true);
      framesDown = framesMove = framesUp = null;
    }
    framesGen++; // cancel any in-flight preload
    framesCache = null;
    framesUrls = null;
    dragState = null;
    unlockScroll();
    var fp = modal.querySelector('.bey3d-fimg');
    if (fp) fp.removeAttribute('src');
    var fr = modal.querySelector('.bey3d-frame');
    if (fr) {
      // REMOVE the frame rather than navigating it to about:blank: navigating
      // would add another joint-session-history entry (see mountViewerFrame),
      // while removal unloads the renderer realm without touching history.
      try { fr.remove(); } catch (e) { /* ignore */ }
    }
    modal.classList.add('bey3d-hidden');
    modal = null; // element persists hidden; at most one renderer realm ever
    if (pushedHistory && !fromPop) history.back(); // consume the viewer's entry
    pushedHistory = false;
    var tile = pill && pill.querySelector('.bey3d-panel');
    if (tile && tile.offsetParent) tile.focus({ preventScroll: true });
  }

  window.addEventListener('message', function (e) {
    if (e.data && e.data.bey3d === 'error') { /* status text shown inside frame */ }
  });

  /* ---------- pill injection + variant tracking ---------- */

  var pill = null;
  var mobileBar = null;
  var windowClose = null;
  var hintHider = null;
  var framesDown = null;
  var framesMove = null;
  var framesUp = null;

  function ensureEntryPoints() {
    var gallery = document.querySelector('media-gallery');
    if (!gallery) return;
    if (!pill) {
      // The 3D entry is its own PANEL in the media grid (Joe ruling
      // 2026-08-18): a full ink tile in slot 2, not an overlay pill. It is our
      // own button element, so the theme's media zoom handlers never match it.
      // The media grid is a <ul> of li.product-media-container cells; the
      // panel is its OWN cell, inheriting the grid item's classes for sizing.
      // On mobile the sized media live in the slideshow (no li), so the panel
      // silently skips there; the mobile bar below must be created regardless.
      // Structural anchor: the first media <li> exists in DOM immediately,
      // while image widths can stay 0 during lazy hydration. The CSS
      // display override keeps the panel visible regardless of media filters.
      var firstLi = gallery.querySelector('li.product-media-container') || gallery.querySelector('li');
      if (firstLi && firstLi.parentElement) {
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'bey3d-panel';
      pill = document.createElement('li');
      pill.className = firstLi.className + ' bey3d-panel-item';
      button.setAttribute('aria-label', 'View ' + (config.product_title || 'this ring') + ' in interactive 3D');
      button.innerHTML =
        '<video class="bey3d-panel-video" muted autoplay loop playsinline aria-hidden="true"></video>' +
        '<div class="bey3d-panel-overlay">' +
        /* one hand glyph sitewide (design review, 2026-08-23): the globe hint's
           drawing replaces the stock Material touch icon */
        '<svg class="bey3d-hand" width="32" height="37" viewBox="0 0 40 46" fill="none" aria-hidden="true">' +
        '<path d="M14 20V7.5a3 3 0 0 1 6 0V19m0-2.5a3 3 0 0 1 6 0V20m0-1.5a3 3 0 0 1 6 0V22m0 0a3 3 0 0 1 6 0v6c0 6-4 12-11 12h-3c-4 0-6-1.5-8.5-5L9 33c-1.5-2.2-.4-4.6 2-4.9 1.4-.2 2.6.4 3 1.4V20a3 3 0 0 1 6 0" fill="rgba(242,237,227,0.96)" stroke="rgba(20,17,13,0.5)" stroke-width="1.1" stroke-linejoin="round"/>' +
        '</svg>' +
        '<span>View in 3D</span>' +
        '<span class="bey3d-sub">Drag to see every angle</span>' +
        '</div>';
      var video = button.querySelector('video');
      video.muted = true; // property too: some browsers ignore the attribute for autoplay
      video.addEventListener('error', function () { video.remove(); }); // ink tile fallback
      syncPanelPreview();
      button.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        openViewer();
      });
      pill.appendChild(button);
      firstLi.parentElement.insertBefore(pill, firstLi.nextElementSibling);
      }
    }
    if (!mobileBar) {
      mobileBar = document.createElement('button');
      mobileBar.type = 'button';
      mobileBar.className = 'bey3d-mobile-bar';
      mobileBar.textContent = 'View this ' + (config.product_kind === 'band' ? 'band' : 'ring') + ' in 3D';
      mobileBar.addEventListener('click', openViewer);
      gallery.parentElement.insertBefore(mobileBar, gallery.nextSibling);
    }
    refreshVisibility();
  }

  function firstSizedImg(gallery) {
    var imgs = gallery.querySelectorAll('img');
    for (var i = 0; i < imgs.length; i++) {
      if (imgs[i].getBoundingClientRect().width >= 200) return imgs[i];
    }
    return null;
  }

  // The panel preview shows the SELECTED metal rocking (real captured footage
  // of the exact viewer). Missing preview asset = video removed, ink tile stays.
  function syncPanelPreview() {
    if (!pill) return;
    var video = pill.querySelector('video');
    if (!video) return;
    // This preview tile is hidden by the gallery below 750px. Keep its
    // source unset on phones; the separate mobile 3D button stays available.
    // Clearing a desktop source also stops buffering when the viewport narrows.
    if (window.matchMedia('(max-width: 749px)').matches) {
      if (video.hasAttribute('src')) {
        video.pause();
        video.removeAttribute('src');
        delete video.dataset.src;
        video.load();
      }
      return;
    }
    var profile = variantProfile(currentVariantId);
    var src = profile && config.preview && config.preview[profile.metal];
    if (!src) { video.remove(); return; }
    if (video.dataset.src !== src) {
      video.dataset.src = src;
      video.src = src;
      video.play && video.play().catch(function () {});
    }
  }

  function refreshVisibility() {
    var has = !!variantProfile(currentVariantId);
    if (pill) {
      pill.classList.toggle('bey3d-hidden', !has);
      syncPanelPreview();
    }
    if (mobileBar) mobileBar.classList.toggle('bey3d-hidden', !has);
  }

  function trackVariant() {
    var id = selectedVariantId();
    if (id !== currentVariantId) {
      currentVariantId = id;
      if (modal) closeViewer(); // profile changed: dispose, reopen builds the new one
      refreshVisibility();
    }
  }

  function init() {
    try {
      currentVariantId = selectedVariantId();
      ensureEntryPoints();
      setInterval(function () {
        try {
          trackVariant();
          // The engine re-renders the gallery on variant navigation and can
          // evict injected nodes; detect eviction and re-inject.
          if (pill && !document.contains(pill)) pill = null;
          if (mobileBar && !document.contains(mobileBar)) mobileBar = null;
          if (!pill || !mobileBar) ensureEntryPoints();
        } catch (e) { /* never break the PDP */ }
      }, 500);
      document.addEventListener('change', function () { setTimeout(trackVariant, 50); });
      window.addEventListener('resize', refreshVisibility);
    } catch (e) { /* never break the PDP */ }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
